/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* ------------------------------------------------------------------------------------------------------------
 * COLLAPSIBLE FILTER SIDEBAR (Trekano)
 *
 * Lets the shopper collapse/expand the entire filter sidebar (not just one group) on collection & search
 * pages. The toggle lives in the "Filters" header; state is remembered across pages via localStorage.
 * The .product-facet element and its header survive the theme's AJAX filter re-render, so a single
 * delegated click handler plus an on-load state apply is all that's needed.
 * ------------------------------------------------------------------------------------------------------------ */
(function () {
  var STORAGE_KEY = 'trk-facet-collapsed';

  function isCollapsed() {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { return false; }
  }

  function applyState(facet, collapsed) {
    facet.classList.toggle('facet-collapsed', collapsed);
    var btn = facet.querySelector('[data-facet-collapse]');
    if (btn) { btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true'); }
  }

  function applyAll() {
    var collapsed = isCollapsed();
    document.querySelectorAll('.product-facet').forEach(function (facet) {
      if (facet.querySelector('[data-facet-collapse]')) { applyState(facet, collapsed); }
    });
  }

  document.addEventListener('click', function (event) {
    var header = event.target.closest && event.target.closest('.product-facet__filters-header');
    var btn = event.target.closest && event.target.closest('[data-facet-collapse]');
    if (!btn && header) { btn = header.querySelector('[data-facet-collapse]'); }
    if (!btn) { return; }

    var facet = btn.closest('.product-facet');
    if (!facet) { return; }

    var next = !facet.classList.contains('facet-collapsed');
    applyState(facet, next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch (e) {}
  });

  if (document.readyState !== 'loading') {
    applyAll();
  } else {
    document.addEventListener('DOMContentLoaded', applyAll);
  }
})();


/* ------------------------------------------------------------------------------------------------------------
 * EMPTY CART BUTTON (Trekano)
 *
 * [data-trk-clear-cart] appears in the cart drawer and on the cart page. Confirms, clears the
 * cart via /cart/clear.js, then tells Focal to re-render (cart:refresh on documentElement).
 * ------------------------------------------------------------------------------------------------------------ */
(function () {
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-trk-clear-cart]');
    if (!btn) { return; }
    if (!window.confirm('Empty your cart? This removes every item.')) { return; }
    btn.setAttribute('disabled', 'disabled');
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, .cart-link__bubble-num, .cart__count, [data-cart-counter]').forEach(function (el) { el.textContent = cart.item_count; });
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        /* On the standalone cart page a reload is the reliable way to show the empty state. */
        if (location.pathname.indexOf('/cart') === 0) { location.reload(); }
        btn.removeAttribute('disabled');
      })
      .catch(function () { btn.removeAttribute('disabled'); location.href = '/cart/clear'; });
  });
})();


/* ------------------------------------------------------------------------------------------------------------
 * FREQUENTLY BOUGHT TOGETHER widget (Trekano) — cart drawer version.
 * Markup renders in sections/cart-recommendations.liquid from custom.goes_well_with of the
 * first cart item. Totals per tick; Add selected adds ticked companions and refreshes the drawer.
 * ------------------------------------------------------------------------------------------------------------ */
(function () {
  function fmt(cents) {
    var n = (cents / 100).toFixed(2).split('.');
    return '$' + n[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + n[1];
  }
  function init(root) {
    if (root.dataset.fbtInit === 'true') { return; }
    root.dataset.fbtInit = 'true';
    var totalEl = root.querySelector('[data-fbt-total]');
    function update() {
      var total = parseInt(root.dataset.mainPrice || 0, 10) || 0;
      root.querySelectorAll('[data-fbt-check]').forEach(function (c) {
        var on = c.checked && !c.disabled;
        if (on) { total += parseInt(c.dataset.price || 0, 10); }
        var th = root.querySelector('[data-fbt-thumb="' + c.value + '"]');
        if (th) { th.classList.toggle('is-off', !on); }
        var pl = root.querySelector('[data-fbt-plus="' + c.value + '"]');
        if (pl) { pl.classList.toggle('is-off', !on); }
      });
      if (totalEl) { totalEl.textContent = fmt(total); }
    }
    root.addEventListener('change', function (e) { if (e.target.closest('[data-fbt-check]')) { update(); } });
    update();
    var btn = root.querySelector('[data-fbt-add]');
    if (!btn) { return; }
    btn.addEventListener('click', function () {
      var items = [];
      var mv = parseInt(root.dataset.mainVariant || '', 10);
      if (mv) { items.push({ id: mv, quantity: 1 }); }
      root.querySelectorAll('[data-fbt-check]').forEach(function (c) {
        if (c.checked && !c.disabled) { items.push({ id: parseInt(c.value, 10), quantity: 1 }); }
      });
      if (!items.length) { return; }
      btn.setAttribute('disabled', 'disabled');
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) { if (!r.ok) { throw new Error('cart add failed'); } return fetch('/cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, .cart-link__bubble-num, .cart__count, [data-cart-counter]').forEach(function (el) { el.textContent = cart.item_count; });
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        })
        .catch(function () { btn.removeAttribute('disabled'); });
    });
  }
  function scan() { document.querySelectorAll('[data-trk-fbt]').forEach(init); }
  if (document.readyState !== 'loading') { scan(); } else { document.addEventListener('DOMContentLoaded', scan); }
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ------------------------------------------------------------------------------------------------------------
 * GORGIAS CHAT BUBBLE → bottom-left (Trekano). The default bottom-right position sits over the
 * buy column on product pages. !important beats the widget's inline styles.
 * ------------------------------------------------------------------------------------------------------------ */
(function () {
  var s = document.createElement('style');
  s.textContent = '#gorgias-chat-container iframe { right: auto !important; left: 16px !important; }';
  document.head.appendChild(s);
})();
